import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { REPORT_VIEW } from '@config/global-constants';
import { update_site_selection } from '@rpt-ngrx/actions/sites.action';
import { selectView } from '@rpt-ngrx/selectors/filters.selector';
import { selectCompanyId } from '@rpt-ngrx/selectors/session.selector';
import { selectSitesAndSelectedSites } from '@rpt-ngrx/selectors/sites.selector';
import { NotificationService, ReportsService } from '@shared/services';
import { IOpenReportHistory } from '@shared/types';
import { ISiteState } from '@shared/types/site';

@Injectable({
    providedIn: 'root',
})
export class SitePickerService {
    private viewState$: Observable<{ [key: string]: string }>;
    private toastId: number | null = null;

    public dependencyMap = {
        Select_View: 'getSelectorConfig',
        Select_View_Hide: 'hideSelector',
    };

    constructor(
        private store: Store,
        protected translations: TranslateService,
        private notificationService: NotificationService,
        private reportsService: ReportsService,
    ) {
        this.viewState$ = this.store.select(selectView);
    }

    public getSelectorConfig(): Observable<{ [key: string]: boolean }> {
        return this.viewState$.pipe(
            distinctUntilChanged(),
            map((data) => {
                const defaultView = REPORT_VIEW.COMPANY_VIEW;
                const { Select_View = defaultView } = data || {};
                const disableSitePicker = Select_View === defaultView;
                return { disableSelector: disableSitePicker };
            }),
        );
    }

    public hideSelector(): Observable<{ [key: string]: boolean }> {
        return this.viewState$.pipe(
            distinctUntilChanged(),
            map((data) => {
                const defaultView = REPORT_VIEW.COMPANY_VIEW;
                const { Select_View = defaultView } = data || {};
                const hideSitePicker = Select_View === defaultView;
                return { hide: hideSitePicker };
            }),
        );
    }

    public checkSiteLimitExceededByProductsCount(): Observable<{ limitExceededError: { type: string; msg: string } }> {
        const warningCondition = 50000;

        return combineLatest([this.store.select(selectSitesAndSelectedSites), this.store.select(selectCompanyId)]).pipe(
            tap(() => {
                if (this.toastId !== null) {
                    this.notificationService.clear(this.toastId);
                    this.toastId = null;
                }
            }),
            switchMap(([{ selectedSites }, companyId]) =>
                this.reportsService.fetchCompanyProducts(companyId, selectedSites).pipe(
                    map((company_products) => {
                        const errorMessage = { type: 'limitExceeded', msg: null };
                        if (warningCondition <= selectedSites.length * company_products.total) {
                            errorMessage.msg = this.translations.instant('WARNINGS.TOO_MANY_SITES');
                            const toast = this.notificationService.warning(
                                this.translations.instant('WARNINGS.TOO_MANY_SITES'),
                            );
                            this.toastId = toast.toastId;
                        }
                        return { limitExceededError: errorMessage };
                    }),
                ),
            ),
        );
    }

    public resetSiteMainFilter(siteState: ISiteState, history: IOpenReportHistory): void {
        if (!history.filters || history.filters.Site_Id === 'None') {
            return;
        }
        const selectedSites =
            history.filters.Site_Id === 'All'
                ? siteState.sites
                : Array.isArray(history.filters.Site_Id)
                  ? siteState.sites.filter((site) =>
                        (history.filters.Site_Id as Array<string>).some((id) => id === site.id || id === site.name),
                    )
                  : [
                        siteState.sites.find(
                            (site) => site.id === history.filters.Site_Id || site.name === history.filters.Site_Id,
                        ),
                    ];
        this.store.dispatch(
            update_site_selection({
                sites: selectedSites.map((site) => {
                    return {
                        id: site.id,
                        name: site.name,
                        type: site.type,
                        business_type: site.business_type,
                        subscriptions: site.subscriptions,
                        subscriptions_data: site.subscriptions_data,
                        aliases: site.aliases,
                        menu_status: site.menu_status,
                    };
                }),
                prevSelectedSites: [],
                prevSelectedSite: null,
                isMultiple: true,
            }),
        );
    }
}
